import {
  Autocomplete,
  Box,
  Checkbox,
  Drawer,
  FilterOptionsState,
  FormControl,
  FormLabel,
  IconButton,
  ListItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import "./SuppliesFilters.scss";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import FilterCifCups from "../../FilterCifCups/FilterCifCups";
import { FiltersSupplies } from "../../../core/models/supplies/supplies.model";
import { FiltersCifCups } from "../../../core/models/filtersCifCups/filtersCifCups.model";
import {
  initialFiltersSupplies,
  removeLocalstorageSupplyFilters,
} from "../../../utils/components/supplies/supplies.util";
import theme from "../../../muiTheme";

export interface SuppliesFiltersProps {
  isFiltersOpen: boolean;
  setIsFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filtersSupplies: FiltersSupplies;
  setFiltersSupplies: (filtersSupplies: FiltersSupplies) => void;
  ownerDataCups: OwnerDataCups[];
  tariffOptions: string[];
}
const SuppliesFilters = ({
  isFiltersOpen,
  setIsFiltersOpen,
  filtersSupplies,
  setFiltersSupplies,
  ownerDataCups,
  tariffOptions,
}: SuppliesFiltersProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useMediaQuery("(max-width: 500px )");
  const [firstInputChanged, setFirstInputChanged] = useState<string>("");
  const [drawerFiltersSupplies, setDrawerFiltersSupplies] =
    useState<FiltersSupplies>(filtersSupplies);
  const [drawerFiltersCifCups, setDrawerFiltersCifCups] =
    useState<FiltersCifCups>(drawerFiltersSupplies);
  const locations = [...new Set(ownerDataCups.map(({ town }) => town))].sort();

  useEffect(() => {
    if (isFiltersOpen) {
      setDrawerFiltersSupplies(filtersSupplies);
    }

    setDrawerFiltersSupplies((prevDrawerFiltersSupplies) => ({
      ...prevDrawerFiltersSupplies,
      ...drawerFiltersCifCups,
    }));
  }, [drawerFiltersCifCups, filtersSupplies, isFiltersOpen]);

  const handleFilterOptions = (
    options: string[],
    { inputValue }: FilterOptionsState<string>
  ) => {
    const value = inputValue.trim().toLowerCase();

    if (value.length > 1) {
      return options.filter((option) =>
        option.toLowerCase().includes(value.toLowerCase())
      );
    }

    return options;
  };

  return (
    <Drawer
      open={isFiltersOpen}
      PaperProps={
        isMobileViewport
          ? { sx: { width: "100%" } }
          : { sx: { width: "31rem" } }
      }
    >
      <Box
        component="div"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={{ xs: "1.5rem 1rem", md: "1.5rem 2.25rem" }}
        className="supplies-filter-box"
      >
        <Typography component="h3" variant="h3">
          {t("Tablas.Filtros")}
        </Typography>
        <IconButton disableRipple onClick={() => setIsFiltersOpen(false)}>
          <span className="t-icon-close brand-red" />
        </IconButton>
      </Box>
      <FilterCifCups
        isFiltersOpen={isFiltersOpen}
        ownerDataCups={ownerDataCups}
        filters={filtersSupplies}
        drawerFilters={drawerFiltersCifCups}
        setDrawerFilters={setDrawerFiltersCifCups}
        firstInputChanged={firstInputChanged}
        setFirstInputChanged={setFirstInputChanged}
        tariffOptions={tariffOptions}
      />
      <FormControl
        sx={{
          padding: { md: "2rem 2.25rem 6.5rem", xs: "2rem 1rem 6.5rem" },
        }}
      >
        <FormLabel sx={{ fontWeight: "600", paddingBottom: ".85rem" }}>
          {t(`Tablas.Localidad`)}
        </FormLabel>
        <Box>
          <Autocomplete
            options={locations}
            autoComplete={
              drawerFiltersSupplies.location &&
              drawerFiltersSupplies.location.length > 1
                ? true
                : false
            }
            value={drawerFiltersSupplies.location}
            noOptionsText={t("Tablas.No hay coincidencias")}
            filterOptions={handleFilterOptions}
            onChange={(event, value, reson) => {
              if (value) {
                setDrawerFiltersSupplies((prevNewAddressData) => ({
                  ...prevNewAddressData,
                  location: value,
                }));
              }
            }}
            renderOption={(props, option, { selected }) => (
              <ListItem {...props}>
                <Checkbox
                  icon={<span className="t-icon-checkbox-default" />}
                  checkedIcon={
                    <span className="t-icon t-icon-checkbox-checked" />
                  }
                  sx={{ fontSize: "24px" }}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </ListItem>
            )}
            sx={{
              // maxWidth: { md: "347px" },
              width: "100%",
            }}
            renderInput={(params) => (
              <TextField
                sx={{ width: "100%" }}
                inputProps={{
                  ...params.inputProps,
                  style: { boxShadow: "none" },
                }}
                InputProps={{
                  ...params.InputProps,
                  fullWidth: true,
                  startAdornment: !!drawerFiltersSupplies.location?.length && (
                    <IconButton
                      sx={{ position: "absolute", right: "2rem" }}
                      onClick={() =>
                        setDrawerFiltersSupplies(
                          (prevDrawerFiltersSupplies) => ({
                            ...prevDrawerFiltersSupplies,
                            location: "",
                          })
                        )
                      }
                    >
                      <span className="t-icon-close" />
                    </IconButton>
                  ),
                  endAdornment: (
                    <Typography
                      component="span"
                      className="t-custom-dropdown t-icon-arrow-down"
                      data-testid="dropdown-arrow"
                      sx={{
                        color: "primary.main",
                        position: "absolute",
                        right: "12px",
                        pointerEvents: "none",
                      }}
                    />
                  ),
                  placeholder: t("Tablas.Selecciona una localidad"),
                }}
              />
            )}
          />
        </Box>
      </FormControl>
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        component="div"
        justifyContent="space-between"
        alignItems="center"
        padding={isMobileViewport ? "0.75rem 1rem" : "0.75rem 2.5rem"}
        position="fixed"
        width={isMobileViewport ? "100%" : "31rem"}
        boxSizing="border-box"
        bottom="0"
        gap={"1rem"}
        bgcolor={theme.palette.background.paper}
        className="bottom-supplies-filters"
      >
        <ButtonElement
          onClick={() => {
            setDrawerFiltersSupplies(initialFiltersSupplies);
            setDrawerFiltersCifCups(initialFiltersSupplies);
            removeLocalstorageSupplyFilters();
          }}
          styles={{ flexGrow: isMobileViewport ? "1" : "auto" }}
          link
        >
          {t("Tablas.Borrar filtros")}
        </ButtonElement>
        <ButtonElement
          onClick={() => {
            setFiltersSupplies(drawerFiltersSupplies);
            setIsFiltersOpen(false);
          }}
        >
          {t("Tablas.Aplicar filtros")}
        </ButtonElement>
      </Stack>
    </Drawer>
  );
};

export default SuppliesFilters;
